import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero, LightSection } from '@/components/PageShell';
import { TableOfContents } from '@/components/content/TableOfContents';
import { FundTheGapCTA } from '@/components/content/FundTheGapCTA';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';
import { findSportGuide, sportGuides } from '@/lib/data/sportGuides';
import { championships } from '@/lib/data/championships';
import { grants } from '@/lib/data/grants';

const usd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export default function SportFundraisingGuide() {
  const { sportSlug } = useParams();
  const guide = findSportGuide(sportSlug);

  // Hooks must run unconditionally; meta for an unknown slug is replaced as
  // soon as the redirect below lands on the index.
  usePageMeta(
    guide ? `How to Fundraise for ${guide.name}` : 'Fundraising guides by sport',
    guide?.description,
    guide
      ? {
          article: true,
          image: guide.image,
          breadcrumbs: [
            { name: 'Resources', path: '/resources' },
            { name: 'Fundraising guides', path: '/resources/fundraising-guides' },
            { name: guide.name },
          ],
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: guide.faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        }
      : {},
  );

  if (!guide) return <Navigate to="/resources/fundraising-guides" replace />;

  const thisMonth = new Date().toISOString().slice(0, 7);
  const events = championships
    .filter((c) => c.sport === guide.sport && (c.end ?? c.start ?? c.month).slice(0, 7) >= thisMonth)
    .sort((a, b) => (a.start ?? a.month).localeCompare(b.start ?? b.month));
  const sportGrants = grants.filter((g) => g.sports.includes(guide.sport));
  const anySportGrants = grants.filter((g) => g.sports.length === 0 && g.applyAs !== 'organization').slice(0, 3);
  const monthly = Math.round(guide.sampleGoal.total / guide.sampleGoal.months);
  const supportersAt25 = Math.ceil(monthly / 25);
  const others = sportGuides.filter((g) => g.slug !== guide.slug);

  const sections = [
    { id: 'what-it-costs', text: `What a ${guide.noun} season costs` },
    { id: 'when-money-is-needed', text: 'When the money is needed' },
    { id: 'money-moments', text: 'The money moments to plan for' },
    { id: 'fundraising-ideas', text: `Fundraising ideas that fit ${guide.name.toLowerCase()}` },
    { id: 'sample-goal', text: 'A sample season goal' },
    ...(events.length ? [{ id: 'nationals', text: 'National championships' }] : []),
    { id: 'grants', text: 'Grants worth checking' },
    { id: 'faq', text: 'Frequently asked questions' },
  ];

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow={`Fundraising guide · ${guide.name}`}
          title={
            <>
              How to fundraise for{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                {guide.noun}.
              </span>
            </>
          }
          lead={guide.intro}
          size="sm"
        />
      }
    >
      <LightSection>
        <div className="max-w-3xl mx-auto">
          <Link to="/resources/fundraising-guides" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            All sport guides
          </Link>
          <article className="prose prose-lg max-w-none">
            <TableOfContents sections={sections} />

            <h2 id="what-it-costs">What a {guide.noun} season costs</h2>
            <p>
              For a competitive or club athlete, plan on roughly <strong>{guide.costRange}</strong> a year.
              {guide.averageSpend && <> {guide.averageSpend}</>}
            </p>
            <div className="not-prose overflow-x-auto">
              <table className="w-full text-left text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="bg-slate-50 text-slate-900">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Line item</th>
                    <th className="px-4 py-2 font-semibold">Typical cost</th>
                  </tr>
                </thead>
                <tbody>
                  {guide.costLines.map((l) => (
                    <tr key={l.item} className="border-t border-slate-200">
                      <td className="px-4 py-2 text-slate-700">{l.item}</td>
                      <td className="px-4 py-2 text-slate-900 font-medium">{l.cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 id="when-money-is-needed">When the money is needed</h2>
            <ul>
              {guide.calendar.map((c) => (
                <li key={c.when}>
                  <strong>{c.when}:</strong> {c.what}
                </li>
              ))}
            </ul>

            <h2 id="money-moments">The money moments to plan for</h2>
            <p>These are the points in a {guide.noun} year when a family suddenly needs cash — and the best moments to ask for support, because there's a specific, believable reason.</p>
            <ul>
              {guide.moneyMoments.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>

            <h2 id="fundraising-ideas">Fundraising ideas that fit {guide.name.toLowerCase()}</h2>
            {guide.angles.map((a) => (
              <div key={a.title}>
                <h3>{a.title}</h3>
                <p>{a.body}</p>
              </div>
            ))}

            <h2 id="sample-goal">A sample season goal</h2>
            <p>
              A goal people trust is itemized and specific. Here's what a {guide.noun} goal of{' '}
              <strong>{usd(guide.sampleGoal.total)}</strong> over {guide.sampleGoal.months} months could look like:
            </p>
            <ul>
              {guide.sampleGoal.lines.map((l) => (
                <li key={l.item}>
                  {l.item}: <strong>{l.cost}</strong>
                </li>
              ))}
            </ul>
            <p>
              That's about <strong>{usd(monthly)} a month</strong> — or roughly <strong>{supportersAt25} supporters giving $25 a month</strong>.
              Write the line items on the fund page, so a grandparent can see their $25 is "one tournament entry," not a
              vague donation. See{' '}
              <Link to="/resources/how-to-set-a-fundraising-goal">how to set a fundraising goal</Link> for the full
              method.
            </p>

            {events.length > 0 && (
              <>
                <h2 id="nationals">National championships</h2>
                <p>If your athlete qualifies, these are the trips to budget for:</p>
                <ul>
                  {events.map((c) => (
                    <li key={c.name}>
                      <a href={c.url} target="_blank" rel="noopener noreferrer">{c.name}</a> — {c.when}, {c.location}
                    </li>
                  ))}
                </ul>
                <p>
                  Use the <Link to="/resources/national-championships-calendar#trip-budget">nationals trip budget calculator</Link>{' '}
                  to price the trip.
                </p>
              </>
            )}

            <h2 id="grants">Grants worth checking</h2>
            <ul>
              {[...sportGrants, ...anySportGrants].map((g) => (
                <li key={g.name}>
                  <a href={g.url} target="_blank" rel="noopener noreferrer">{g.name}</a> — {g.covers}
                </li>
              ))}
            </ul>
            <p>
              The full list, with who qualifies and how to apply, is in the{' '}
              <Link to="/resources/youth-sports-grants">youth sports grants directory</Link>.
            </p>

            <FundTheGapCTA title={`Start a free ${guide.name.toLowerCase()} fund page`} />

            <h2 id="faq">Frequently asked questions</h2>
            <div className="not-prose space-y-6">
              {guide.faqs.map((f) => (
                <div key={f.q}>
                  <h3 className="font-semibold text-lg text-slate-900 mb-1">{f.q}</h3>
                  <p className="text-slate-600">{f.a}</p>
                </div>
              ))}
            </div>

            <p className="text-sm text-slate-500 mt-10">
              Sources:{' '}
              {guide.sources.map((s, i) => (
                <span key={s.url}>
                  {i > 0 && '; '}
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-slate-500">
                    {s.label}
                  </a>
                </span>
              ))}
              . Costs vary widely by region, club and level; figures are ranges, not quotes.
            </p>

            <h2>Guides for other sports</h2>
            <div className="not-prose flex flex-wrap gap-2">
              {others.map((g) => (
                <Link
                  key={g.slug}
                  to={`/resources/fundraising-guides/${g.slug}`}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
                >
                  {g.name}
                </Link>
              ))}
            </div>
            <ArticleFeedback articleSlug={`fundraising-guides/${guide.slug}`} />
          </article>
        </div>
      </LightSection>
    </PageShell>
  );
}
